// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

import { OPERATIONAL_API_URL } from '../config/externalApis';

export interface ReprocessDepositAttestationRequest {
  startTime: string;
  endTime: string;
  batchSize?: number;
}

export interface BlacklistedUser {
  id: number;
  partyId: string;
  reason: string;
  createdAt: string;
  deletedAt: string | null;
}

export interface BlacklistUsersRequest {
  blacklistedUsers: { partyId: string; reason: string }[];
}

const authHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Request failed (${response.status}): ${text}`);
  }
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json() as Promise<T>;
  }
  return undefined as unknown as T;
};

export const reprocessDepositAttestations = async (
  token: string,
  request: ReprocessDepositAttestationRequest,
): Promise<string[]> => {
  const response = await fetch(`${OPERATIONAL_API_URL}/reprocess-deposit-attestations`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(request),
  });
  return handleResponse<string[]>(response);
};

export const getBlacklistedUsers = async (token: string): Promise<BlacklistedUser[]> => {
  const response = await fetch(`${OPERATIONAL_API_URL}/users/blacklist`, {
    method: 'GET',
    headers: authHeaders(token),
  });
  return handleResponse<BlacklistedUser[]>(response);
};

export const blacklistUsers = async (
  token: string,
  request: BlacklistUsersRequest,
): Promise<void> => {
  const response = await fetch(`${OPERATIONAL_API_URL}/users/blacklist`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(request),
  });
  await handleResponse<void>(response);
};

export const removeUsersFromBlacklist = async (
  token: string,
  partyIds: string[],
): Promise<void> => {
  const response = await fetch(`${OPERATIONAL_API_URL}/users/blacklist`, {
    method: 'DELETE',
    headers: authHeaders(token),
    body: JSON.stringify(partyIds),
  });
  await handleResponse<void>(response);
};
